import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiPaperclip } from 'react-icons/fi';
import AppLayout from '../components/layout/AppLayout';
import Button from '../components/common/Button';
import Icon from '../components/common/Icon';
import { useUI } from '../context/UIContext';
import { ROUTES } from '../constants/constants';

const FORMS = [
  { id: 'spice', form: 'SPICe+ (INC-32)', category: 'incorporation', title: 'Simplified incorporation, MoA, AoA, PAN, TAN, DIN', who: 'All new companies', deadline: 'At incorporation', fee: 'Nil up to ₹15L capital; slab above', penalty: '—', priority: 'info' },
  { id: 'inc20a', form: 'INC-20A', category: 'incorporation', title: 'Declaration for Commencement of Business', who: 'Companies incorporated on/after 2 Nov 2018', deadline: '180 days from incorporation', fee: '₹200–₹600', penalty: '₹50,000 co + ₹1,000/day officer (cap ₹1,00,000)', priority: 'critical' },
  { id: 'aoc4', form: 'AOC-4', category: 'annual', title: 'Filing of Financial Statements', who: 'All companies', deadline: '30 days from AGM', fee: '₹200–₹600', penalty: '₹100/day (no max cap)', priority: 'critical' },
  { id: 'mgt7', form: 'MGT-7', category: 'annual', title: 'Annual Return', who: 'All except OPC/Small Co.', deadline: '60 days from AGM', fee: '₹200–₹600', penalty: '₹100/day (no max cap)', priority: 'critical' },
  { id: 'mgt7a', form: 'MGT-7A', category: 'annual', title: 'Annual Return (OPC / Small Co.)', who: 'OPC, Small Companies', deadline: '60 days from AGM', fee: '₹200', penalty: '₹100/day (no max cap)', priority: 'critical' },
  { id: 'adt1', form: 'ADT-1', category: 'annual', title: 'Appointment/Reappointment of Statutory Auditor', who: 'All companies', deadline: '15 days from AGM', fee: '₹200–₹600', penalty: '1×–12× fee by delay', priority: 'warn' },
  { id: 'dir3kyc', form: 'DIR-3 KYC / Web', category: 'director', title: 'KYC for DIN holders (triennial from FY 2026)', who: 'All DIN holders', deadline: '30 Jun, every 3rd FY (first: 30 Jun 2028)', fee: 'Nil on time · ₹500 detail update', penalty: 'DIN deactivated, ₹5,000 late/reactivation', priority: 'critical' },
  { id: 'dir12', form: 'DIR-12', category: 'director', title: 'Appointment / change in directors and KMP', who: 'All companies', deadline: '30 days from change', fee: '₹200–₹600', penalty: '2×–12× fee by delay; + Sec 172 penalty (no per-day accrual)', priority: 'warn' },
  { id: 'sh7', form: 'SH-7', category: 'event', title: 'Increase in Authorised Capital', who: 'All companies', deadline: '30 days from ordinary resolution', fee: '₹200–₹600 + capital slab & stamp duty', penalty: '₹500/day; cap ₹5L co / ₹1L officer', priority: 'warn' },
  { id: 'pas3', form: 'PAS-3', category: 'event', title: 'Return of Allotment of Shares', who: 'All companies', deadline: '30 days; 15 days private placement', fee: '₹200–₹600', penalty: '₹1,000/day, cap ₹1,00,000 (Sec 39(5))', priority: 'warn' },
  { id: 'mgt14', form: 'MGT-14', category: 'event', title: 'Filing prescribed board/shareholder resolutions', who: 'All companies', deadline: '30 days from resolution/agreement', fee: '₹200–₹600', penalty: '₹10,000 + ₹100/day; cap ₹2L co / ₹50k officer', priority: 'warn' },
  { id: 'chg1', form: 'CHG-1', category: 'charges', title: 'Creation / Modification of Charge', who: 'Companies with secured charge', deadline: '30 days; max 120 days with fees', fee: '₹200–₹600; 6× + ad valorem if late', penalty: '₹5,00,000 co + ₹50,000 officer (Sec 86); charge unsecured', priority: 'warn' },
  { id: 'chg4', form: 'CHG-4', category: 'charges', title: 'Satisfaction of Charge', who: 'Companies with charge satisfaction', deadline: '30 days; to 300 days on application', fee: '₹200–₹600', penalty: '₹5,00,000 co + ₹50,000 officer (Sec 86)', priority: 'info' },
  { id: 'llp11', form: 'LLP Form 11', category: 'llp', title: 'Annual Return (LLP)', who: 'All LLPs', deadline: '30 May annually', fee: '₹50–₹600', penalty: '1×–30× fee by delay (2022 slabs); +₹10–20/day past 360d', priority: 'critical' },
  { id: 'llp8', form: 'LLP Form 8', category: 'llp', title: 'Statement of Accounts and Solvency', who: 'All LLPs', deadline: '30 Oct annually', fee: '₹50–₹600', penalty: '1×–30× fee by delay (2022 slabs); +₹10–20/day past 360d', priority: 'critical' },
  { id: 'fillip', form: 'FiLLiP', category: 'llp', title: 'LLP Incorporation Form', who: 'New LLPs', deadline: 'At incorporation', fee: '₹500–₹5,000 by contribution', penalty: '—', priority: 'info' },
  { id: 'stk2', form: 'STK-2', category: 'winding', title: 'Application for strike off by company', who: 'Dormant/defunct companies', deadline: 'On application', fee: '₹10,000', penalty: '—', priority: 'info' },
  { id: 'gnl1', form: 'GNL-1', category: 'winding', title: 'General application before ROC', who: 'As required', deadline: 'On requirement', fee: '₹200–₹600', penalty: '—', priority: 'info' },
];

const CATEGORIES = [
  { id: 'all', label: 'All Forms' },
  { id: 'incorporation', icon: '📝', label: 'Incorporation' },
  { id: 'annual', icon: '📅', label: 'Annual' },
  { id: 'event', icon: '⚡', label: 'Event-Based' },
  { id: 'charges', icon: '🏦', label: 'Charges' },
  { id: 'director', icon: '👔', label: 'Directors' },
  { id: 'llp', icon: '🤝', label: 'LLP' },
  { id: 'winding', icon: '❌', label: 'Strike Off/Winding' },
];

const PRIORITY_STYLE = {
  critical: { bg: 'bg-[#f2e9e6]', text: 'text-[#9a564a]' },
  warn:     { bg: 'bg-[#f3ebd7]', text: 'text-[#8a6a1f]' },
  info:     { bg: 'bg-[#edeef3]', text: 'text-[#3a4a66]' },
};

export default function MCAFormsPage() {
  const { showToast } = useUI();
  const location = useLocation();
  const navigate = useNavigate();
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');

  // Deep-link: pre-fill search from spotlight search
  useEffect(() => {
    const q = location.state?.search;
    if (!q) return;
    setSearch(q);
    window.history.replaceState({}, '');
  }, [location.state?.search]);

  const filtered = FORMS.filter((f) => {
    const matchCat = category === 'all' || f.category === category;
    const q = search.toLowerCase();
    const matchSearch = !q || f.form.toLowerCase().includes(q) || f.title.toLowerCase().includes(q) || f.who.toLowerCase().includes(q);
    return matchCat && matchSearch;
  });

  const handleGuide = (form) => {
    showToast(`Opening filing guide for ${form} — Ask ComplyV AI for step-by-step help`, 'info');
  };

  return (
    <AppLayout pageTitle="MCA Forms Help Kit">
      <div className="text-[24px] font-bold text-gray-900 mb-1" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        MCA Forms Help Kit — All ROC Forms
      </div>
      <div className="text-[13px] text-gray-500 mb-[24px]">
        Purpose, applicability, fees, due dates and help links for MCA/ROC forms as per Companies Act 2013 and LLP Act 2008
      </div>

      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <input
          type="text"
          placeholder="Search by form name, purpose, or applicability..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[260px] px-4 py-2 rounded-lg border border-gray-200 text-[12.5px] outline-none focus:border-[#145f5f]"
        />
      </div>

      <div className="flex gap-2 flex-wrap mb-[18px]">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-[13px] py-[5px] rounded-[20px] border text-[11.5px] font-semibold transition-all inline-flex items-center gap-[5px] ${
                category === c.id ? 'bg-[#145f5f] text-white border-[#145f5f]' : 'bg-white border-gray-200 text-gray-600 hover:border-[#1e8a8a] hover:text-[#145f5f]'
              }`}
            >
              {c.icon && <Icon name={c.icon} size={12} />}
              {c.label}
            </button>
          ))}
      </div>

      <div className="bg-white rounded-[12px] border overflow-hidden" style={{ borderColor: '#ddd6c6', boxShadow: '0 2px 16px rgba(8,17,31,0.07)' }}>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-[#f8f5ef] border-b" style={{ borderColor: '#ddd6c6' }}>
                {['Form', 'Purpose', 'Applicability', 'Due Date / Trigger', 'Govt Fee', 'Penalty', 'Priority', 'Help'].map((h) => (
                  <th key={h} className="text-left px-[14px] py-[10px] text-[10.5px] uppercase tracking-[1px] text-[#7a7060] font-bold whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => {
                const ps = PRIORITY_STYLE[f.priority];
                return (
                  <tr key={f.id} className="border-b last:border-0 hover:bg-[#f8f5ef]" style={{ borderColor: '#ddd6c6' }}>
                    <td className="px-[14px] py-[11px]">
                      <span className="text-[12.5px] font-bold text-teal-700" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.form}</span>
                    </td>
                    <td className="px-[14px] py-[11px] text-[12.5px] font-semibold text-[#08111f] max-w-[260px]">{f.title}</td>
                    <td className="px-[14px] py-[11px] text-[11.5px] text-[#7a7060]">{f.who}</td>
                    <td className="px-[14px] py-[11px] text-[11.5px] text-[#08111f] whitespace-nowrap">{f.deadline}</td>
                    <td className="px-[14px] py-[11px] text-[11.5px] text-[#7a7060]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{f.fee}</td>
                    <td className="px-[14px] py-[11px] text-[11.5px] text-[#9a564a] font-semibold">{f.penalty}</td>
                    <td className="px-[14px] py-[11px]">
                      <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full ${ps.bg} ${ps.text}`}>{f.priority}</span>
                    </td>
                    <td className="px-[14px] py-[11px]">
                      <Button
                        variant="soft"
                        size="xs"
                        className="whitespace-nowrap"
                        leftIcon={<FiPaperclip size={14} />}
                        onClick={() => handleGuide(f.form)}
                      >
                        MCA Guide
                      </Button>
                      {/* The one form ComplyV can prepare today. */}
                      {f.id === 'mgt7a' && (
                        <Button
                          variant="outline"
                          size="xs"
                          className="whitespace-nowrap ml-1.5"
                          onClick={() => navigate(ROUTES.MCA_PREPARE)}
                        >
                          Prepare in ComplyV
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-[13px] text-gray-400">
                    No forms match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-[#ddd6c6] bg-[#f8f5ef] px-4 py-3 text-[11.5px] text-[#7a7060]">
        For latest notifications and form updates, verify on MCA e-filing portal before filing. Source references: MCA21, Companies Act 2013, LLP Act 2008.
      </div>
    </AppLayout>
  );
}
